import React, { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { loginThunk, useAppDispatch, useAppSelector } from "../redux";
import { Button } from "../components/common";
import { ROUTES } from "../constants";
import { showSuccess } from "../services";
import { UserRole } from "../types";
import aksharArtLogoFull from "../assets/logos/akshar-art-logo-full.svg";
import "./Login.css";

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [role, setRole] = useState<UserRole>("teacher");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { isAuthenticated, role: activeRole } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated && activeRole) {
      if (activeRole === "teacher") {
        navigate(ROUTES.TEACHER.DASHBOARD, { replace: true });
      } else if (activeRole === "student") {
        navigate(ROUTES.STUDENT.DASHBOARD, { replace: true });
      }
    }
  }, [isAuthenticated, activeRole, navigate]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    dispatch(loginThunk({ email, password, role }))
      .unwrap()
      .then((res) => {
        showSuccess("Login successful.");
        if (res.role === "teacher") {
          navigate(ROUTES.TEACHER.DASHBOARD, { replace: true });
        } else if (res.role === "student") {
          navigate(ROUTES.STUDENT.DASHBOARD, { replace: true });
        }
      })
      .catch(() => {
        // Error toast is automatically displayed by errorMiddleware
      });
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo-wrapper">
          <img
            src={aksharArtLogoFull}
            alt="Akshar Art Portal Logo"
            className="login-logo"
          />
        </div>

        <div className="login-header">
          <h2>Welcome Back</h2>
          <p>Sign in to access the Akshar Art Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="role-selector-container">
            <label className="role-selector-label">Select Role</label>
            <div className="role-selector-toggle" role="radiogroup" aria-label="User Role Selection">
              <button
                type="button"
                className={`role-option-btn ${role === "student" ? "active" : ""}`}
                onClick={() => setRole("student")}
                aria-checked={role === "student"}
                role="radio"
              >
                Student
              </button>
              <button
                type="button"
                className={`role-option-btn ${role === "teacher" ? "active" : ""}`}
                onClick={() => setRole("teacher")}
                aria-checked={role === "teacher"}
                role="radio"
              >
                Teacher
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">
              {role === "student" ? "Email Address / User ID" : "Email Address"}
            </label>
            <input
              type="text"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={role === "student" ? "Enter your email or user ID" : "Enter your email"}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="large"
            style={{ width: "100%", marginTop: "8px" }}
          >
            Sign In to Portal
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Login;
